import {
  Calendar} from "lucide-react";
import React, { useEffect, useState } from "react";
import { getDisciplineIcon } from "../constants";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { BookOpen, Filter, Search } from "lucide-react";
import api from "../api/api";
import authService from "../services/authService";
import ProgramActions from "./ProgramActions";

const ClientDetails = () => {
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    discipline: "",
    month: "",
    keyword: "",
  });

  
  const { clientId } = useParams();
  const navigate = useNavigate();
  const isTherapist = authService.isTherapist();

  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/client_details/?id=${clientId}`);
        setClientData(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch client details");
        setLoading(false);
        console.error("Error fetching client details:", err);
      }
    };

    fetchClientDetails();
  }, [clientId]);
  

  const handleProgramDelete = async (programId) => {
    try {
      setClientData((prevData) => ({
        ...prevData,
        programs: prevData.programs.filter((p) => p.id !== programId),
      }));
    } catch (error) {
      setError("Error deleting program. Please try again later.");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/programs/${programToDelete.id}/`);
      setClientData((prevData) => ({
        ...prevData,
        programs: prevData.programs.filter((p) => p.id !== programToDelete.id),
      }));
      setShowDeleteModal(false);
      setProgramToDelete(null);
    } catch (error) {
      setError("Error deleting program. Please try again later.");
    }
  };

  const handleViewEditClient = () => {
    window.location.href = `/client/edit/${clientId}`;
  };

  const handleAddProgram = () => {
    window.location.href = `/program/add/${clientId}`;
  };

  const handleProgramOpen = (programId) => {
    const today = format(new Date(), "yyyy-MM-dd");
    navigate(`/program/${programId}?date=${today}`);
  };

  const handleProgramCardKeyDown = (event, programId) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleProgramOpen(programId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString();
  };

  const filterPrograms = (status) => {
    if (!clientData?.programs) return [];
    const currentDate = new Date();
    // Strip time component for consistent date comparison
  
    let filteredPrograms = clientData.programs.filter((program) => {
      let startDate = null;
      if (program.start_date) {
        startDate = new Date(program.start_date);
        // Strip time component
        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      }
      
      let endDate = null;
      if (program.end_date) {
        endDate = new Date(program.end_date);
        // Strip time component
        endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      }
      if (status === "active") {
        return (
          startDate &&
          startDate <= currentDate &&
          (!endDate || endDate >= currentDate)
        );
      } else {
        return (
          (startDate && startDate > currentDate) ||
          (endDate && endDate < currentDate)
        );
      }
    });

    // Apply additional filters for past programs
    if (status === "past") {
      if (filters.discipline) {
        filteredPrograms = filteredPrograms.filter(
          (program) => program.therapist_discipline === filters.discipline
        );
      }

      if (filters.month) {
        filteredPrograms = filteredPrograms.filter((program) => {
          const programDate = new Date(program.end_date);
          return programDate.getMonth() === parseInt(filters.month);
        });
      }

      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        filteredPrograms = filteredPrograms.filter((program) =>
          program.name.toLowerCase().includes(keyword)
        );
      }
    }

    return filteredPrograms;
  };

  const getDisciplines = () => {
    if (!clientData?.programs) return [];
    return [...new Set(clientData.programs.map((p) => p.therapist_discipline))];
  };

  const renderFilters = () => {
    return (
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <Filter className="me-2" size={20} /> Filters
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFilters({ discipline: "", month: "", keyword: "" });
                setShowFilters(false);
              }}
            >
              Clear All Filters
            </Button>
          </div>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Discipline</Form.Label>
                <Form.Select
                  value={filters.discipline}
                  onChange={(e) =>
                    setFilters({ ...filters, discipline: e.target.value })
                  }
                >
                  <option value="">All Disciplines</option>
                  {getDisciplines().map((discipline) => (
                    <option key={discipline} value={discipline}>
                      {discipline}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Month</Form.Label>
                <Form.Select
                  value={filters.month}
                  onChange={(e) =>
                    setFilters({ ...filters, month: e.target.value })
                  }
                >
                  <option value="">All Months</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(2024, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Keyword</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search programs..."
                    value={filters.keyword}
                    onChange={(e) =>
                      setFilters({ ...filters, keyword: e.target.value })
                    }
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  const getDayLabel = (day) => {
    const dayMap = {
      MON: "M",
      TUE: "Tu",
      WED: "W",
      THU: "Th",
      FRI: "F",
      SAT: "Sa",
      SUN: "Su",
    };
    return dayMap[day] || day;
  };

  const renderProgramList = (programs) => {
    if (programs.length === 0) {
      return (
        <Card className="text-center p-5 border-0 shadow-sm">
          <BookOpen size={48} className="mx-auto mb-3 text-muted" />
          <p className="text-muted mb-0">No programs found in this category.</p>
        </Card>
      );
    }

    return (
      <Row xs={1} md={2} lg={3} className="g-4 mb-4">
        {programs.map((program, index) => {
          return (
            <Col key={index}>
              <Card
                className="h-100 shadow-sm hover:shadow-md transition-all duration-200 border-0 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Open ${program.name}`}
                onClick={() => handleProgramOpen(program.id)}
                onKeyDown={(event) =>
                  handleProgramCardKeyDown(event, program.id)
                }
              >
                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start">
                    <Card.Title className="mb-3">{program.name}</Card.Title>
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <ProgramActions
                        program={program}
                        clientId={clientId}
                        isTherapist={isTherapist}
                        onProgramDelete={handleProgramDelete}
                      />
                    </div>
                  </div>

                  <div className="mt-3 text-start" style={{ fontSize: "14px" }}>
                    <div
                      className="mb-1 text-start"
                      title="Program last completed on"
                    >
                      <Calendar size={16} className="text-primary me-2" />
                      {formatDate(program.last_completed_at)}
                    </div>

                    <div className="my-1 text-start d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center" title="Discipline">
                        {getDisciplineIcon(program.therapist_discipline)}
                        {program.therapist_discipline}
                      </div>
                      {program.is_draft && (
                        <span className="badge bg-secondary">Draft</span>
                      )}
                    </div>

                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {program.days_of_week?.map((day) => (
                        <Badge
                          key={day}
                          bg="custom-pink"
                          className="me-1 d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            padding: 0
                          }}
                        >
                          {getDayLabel(day)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="text-center mt-5">
        <Card className="p-4 border-danger">
          <h4 className="text-danger">{error}</h4>
        </Card>
      </Container>
    );
  }

  if (!clientData) return null;

  const isPracticeClient = Boolean(clientData.client_profile?.is_practice);

  return (
    <Container className="py-5 mb-5">
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <h2 className="text-center mb-4">
            {isPracticeClient
              ? "Julia Smith"
              : `${clientData.last_name}, ${clientData.first_name}`}
          </h2>

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
            <div className="d-flex gap-3">
              {!isPracticeClient && (
                <Button variant="primary" onClick={handleViewEditClient}>
                  View/Edit Client Details
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleAddProgram}
                data-walkthrough="add-program"
              >
                + Add Program
              </Button>
            </div>
          </div>

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4 d-flex w-100 border-bottom"
          >
            <Tab
              eventKey="active"
              title={
                <div className="d-flex align-items-center">
                  <span className="fs-6">Active</span>
                </div>
              }
              className="flex-grow-1 text-center"
            >
              {renderProgramList(filterPrograms("active"))}
            </Tab>
            <Tab
              eventKey="past"
              title={
                <div className="d-flex align-items-center">
                  <span className="fs-6">Past</span>
                </div>
              }
              className="flex-grow-1 text-center"
            >
              <div className="mb-3">
                {!showFilters ? (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFilters(true)}
                    className="mb-3"
                  >
                    <Filter size={16} className="me-2" />
                    Show Filters
                  </Button>
                ) : (
                  renderFilters()
                )}
              </div>
              {renderProgramList(filterPrograms("past"))}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {programToDelete?.name}? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientDetails;
